/**
 * Phase 1D follow-up — IoC source roll-up table.
 *
 * Replaces the chip-only "Sources observing this IoC" block with a real
 * TanStack Table v8 layout. Columns: Source, Fidelity, Category, First
 * seen, Last seen, Weight, Evidence URL. Rows expand to show the raw
 * jsonb payload from the most recent sighting on that source if present.
 *
 * Pulls its data from the verdict envelope (provenance.sources[]) —
 * `useIoCEnvelope` calls the admin-bff `?envelope=v2` branch. The legacy
 * `sources: string[]` payload from the dialog's main detail call doesn't
 * carry fidelity / weight / first-seen-per-source, so we go envelope or
 * bust here.
 */
import { useState } from "react";
import {
    type ColumnDef,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    type SortingState,
    useReactTable,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";

import { type VerdictSourceEntry, useIoCEnvelope, useIoCSightings } from "~/lib/iocs";

interface Props {
    iocId: string;
    /** Fallback source slugs from the legacy /iocs/{id} payload — shown
     * as a thin chip row when the envelope is still loading or returns
     * no provenance. */
    fallbackSources: string[];
}

export function SourceRollupTable({ iocId, fallbackSources }: Props): React.ReactElement {
    const env = useIoCEnvelope(iocId);
    // Default sort: highest fidelity first (high → med → low), then by
    // last_seen DESC. The fidelity-rank helper below produces the numeric
    // we sort on; column-click swaps in the raw value.
    const [sorting, setSorting] = useState<SortingState>([{ id: "fidelity", desc: true }]);

    const sources = env.data?.provenance?.sources ?? [];

    const columns: ColumnDef<VerdictSourceEntry>[] = [
        {
            id: "expand",
            header: "",
            cell: () => null, // toggle is rendered inline below
            size: 24,
            enableSorting: false,
        },
        {
            id: "source",
            accessorKey: "id",
            header: "Source",
            cell: ({ getValue, row }) => (
                <span className="cluster-xs">
                    <span className="chip chip--accent" style={{ height: 20 }}>
                        {String(getValue<string>())}
                    </span>
                    {row.original.is_national_cert && (
                        <span
                            className="chip chip--info"
                            style={{ height: 20 }}
                            title="National-CERT source — extra trust weight applied"
                        >
                            CERT
                        </span>
                    )}
                </span>
            ),
        },
        {
            id: "fidelity",
            accessorFn: (row) => fidelityRank(row.fidelity),
            header: "Fidelity",
            cell: ({ row }) => (
                <span className={fidelityChipClass(row.original.fidelity)}>
                    {row.original.fidelity}
                </span>
            ),
        },
        {
            id: "category",
            accessorKey: "category",
            header: "Category",
            cell: ({ getValue }) => {
                const v = String(getValue<string>() ?? "");
                return v ? (
                    <span className="text-12 text-text-muted">{v}</span>
                ) : (
                    <span className="text-text-subtle text-12">—</span>
                );
            },
        },
        {
            id: "first_seen",
            accessorKey: "first_seen",
            header: "First seen",
            cell: ({ getValue }) => (
                <span className="text-12 text-text-muted tabular-nums">
                    {fmtDate(String(getValue<string>()))}
                </span>
            ),
        },
        {
            id: "last_seen",
            accessorKey: "last_seen",
            header: "Last seen",
            cell: ({ getValue }) => (
                <span className="text-12 text-text-muted tabular-nums">
                    {fmtDate(String(getValue<string>()))}
                </span>
            ),
        },
        {
            id: "weight",
            accessorKey: "weight",
            header: "Weight",
            cell: ({ getValue }) => {
                const w = Number(getValue<number>() ?? 0);
                return (
                    <span className="tabular-nums text-12">
                        {(w * 100).toFixed(0)}%
                    </span>
                );
            },
        },
        {
            id: "evidence_url",
            accessorKey: "evidence_url",
            header: "Evidence",
            cell: ({ getValue }) => {
                const u = String(getValue<string>() ?? "");
                if (!u) return <span className="text-text-subtle text-12">—</span>;
                return (
                    <a
                        href={u}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="cluster-xs text-12"
                        style={{ color: "var(--color-accent)" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <ExternalLink size={11} aria-hidden /> source
                    </a>
                );
            },
            enableSorting: false,
        },
    ];

    const table = useReactTable({
        data: sources,
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    if (env.isLoading) {
        return (
            <section className="card stack-sm">
                <h3 className="type-h3">Sources observing this IoC</h3>
                <div className="text-12 text-text-muted">Loading source roll-up…</div>
            </section>
        );
    }

    if (sources.length === 0) {
        // Fall back to the chip row from the legacy payload so an envelope
        // miss never shows a totally blank section — operators still want
        // to see SOMETHING.
        return (
            <section className="card stack-sm">
                <h3 className="type-h3">Sources observing this IoC</h3>
                {fallbackSources.length === 0 ? (
                    <span className="text-text-subtle text-12">None recorded.</span>
                ) : (
                    <div className="cluster-xs">
                        {fallbackSources.map((s) => (
                            <span key={s} className="chip chip--accent">
                                {s}
                            </span>
                        ))}
                    </div>
                )}
            </section>
        );
    }

    return (
        <section className="card stack-sm">
            <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                <h3 className="type-h3">Sources observing this IoC</h3>
                <span className="text-12 text-text-muted">
                    {sources.length} source{sources.length === 1 ? "" : "s"}
                    {env.data && env.data.provenance.truncated_count > 0 && (
                        <> · +{env.data.provenance.truncated_count} truncated</>
                    )}
                </span>
            </div>
            <div style={{ overflowX: "auto" }}>
                <table className="data-table" style={{ tableLayout: "auto", width: "100%" }}>
                    <thead>
                        {table.getHeaderGroups().map((hg) => (
                            <tr key={hg.id}>
                                {hg.headers.map((header) => {
                                    const sortDir = header.column.getIsSorted();
                                    const canSort = header.column.getCanSort();
                                    return (
                                        <th
                                            key={header.id}
                                            onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                                            style={{
                                                cursor: canSort ? "pointer" : undefined,
                                                whiteSpace: "nowrap",
                                                userSelect: "none",
                                            }}
                                        >
                                            <span className="cluster-xs">
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(header.column.columnDef.header, header.getContext())}
                                                {canSort && sortDir === "asc" && (
                                                    <ArrowUp size={11} aria-hidden />
                                                )}
                                                {canSort && sortDir === "desc" && (
                                                    <ArrowDown size={11} aria-hidden />
                                                )}
                                            </span>
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map((row) => (
                            <SourceRow key={row.id} row={row} iocId={iocId} />
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

interface SourceRowProps {
    row: ReturnType<ReturnType<typeof useReactTable<VerdictSourceEntry>>["getRowModel"]>["rows"][number];
    iocId: string;
}

/** Per-row component so we can lazy-fetch the sightings raw payload only
 * when the operator expands the row. Sightings are fetched in bulk by
 * the caller — we just filter the result by source slug. */
function SourceRow({ row, iocId }: SourceRowProps): React.ReactElement {
    const [open, setOpen] = useState(false);
    const sightings = useIoCSightings(open ? iocId : null);
    const sourceID = row.original.id;
    const matching = (sightings.data ?? []).filter((s) => s.sourceSlug === sourceID);
    const latestRaw = matching[0]?.raw;

    return (
        <>
            <tr
                onClick={() => setOpen((o) => !o)}
                style={{ cursor: "pointer" }}
                aria-expanded={open}
            >
                {row.getVisibleCells().map((cell) => {
                    if (cell.column.id === "expand") {
                        return (
                            <td key={cell.id} style={{ width: 24 }}>
                                {open ? (
                                    <ChevronDown size={14} aria-hidden />
                                ) : (
                                    <ChevronRight size={14} aria-hidden />
                                )}
                            </td>
                        );
                    }
                    return (
                        <td key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                    );
                })}
            </tr>
            {open && (
                <tr>
                    <td colSpan={row.getVisibleCells().length} style={{ padding: 0 }}>
                        <pre
                            style={{
                                margin: 0,
                                padding: "var(--space-3)",
                                background: "var(--color-surface-sunken)",
                                fontSize: "var(--font-size-11)",
                                fontFamily: "var(--font-family-mono)",
                                color: "var(--color-text-muted)",
                                overflow: "auto",
                                maxHeight: 240,
                            }}
                        >
                            {sightings.isLoading
                                ? "Loading raw payload…"
                                : latestRaw
                                ? JSON.stringify(latestRaw, null, 2)
                                : "No raw payload captured for this source."}
                        </pre>
                    </td>
                </tr>
            )}
        </>
    );
}

function fidelityRank(f: string): number {
    switch (f) {
        case "high":
            return 3;
        case "med":
            return 2;
        case "low":
            return 1;
    }
    return 0;
}

function fidelityChipClass(f: string): string {
    // Reuse the existing status-pill scale: high → success, med → warn,
    // low → muted. A new chip for fidelity would look out of place
    // alongside the severity chip already on the page.
    switch (f) {
        case "high":
            return "status-pill status-pill--live";
        case "med":
            return "status-pill status-pill--paused";
        case "low":
            return "status-pill status-pill--queued";
    }
    return "status-pill";
}

function fmtDate(s: string): string {
    if (!s) return "—";
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleString();
}
